import type { FC, ReactNode } from "react";

import { COLOR_BG, COLOR_TEXT, COLOR_TEXT_EMPHASIS } from "./colorTokens";
import { Text } from "./Text";

/* ------------------------------------------------------------------ */
/*  Variant colours                                                    */
/* ------------------------------------------------------------------ */

type ColorVariant =
  | "default"
  | "emerald"
  | "indigo"
  | "violet"
  | "amber"
  | "rose";

/** Semantic aliases kept for backward compatibility. */
type SemanticVariant = "primary" | "success" | "danger" | "warning";

export type MetricCardVariant = ColorVariant | SemanticVariant;

function resolveVariant(v: MetricCardVariant): ColorVariant {
  switch (v) {
    case "primary": {
      return "indigo";
    }
    case "success": {
      return "emerald";
    }
    case "danger": {
      return "rose";
    }
    case "warning": {
      return "amber";
    }
    default: {
      return v;
    }
  }
}

const COLORS: Record<
  ColorVariant,
  { iconBg: string; iconText: string; valueText: string }
> = {
  amber: {
    iconBg: COLOR_BG.amber,
    iconText: COLOR_TEXT.amber,
    valueText: COLOR_TEXT_EMPHASIS.amber,
  },
  default: {
    iconBg: "bg-slate-100 dark:bg-white/10",
    iconText: "text-slate-500 dark:text-white/60",
    valueText: "text-slate-900 dark:text-white",
  },
  emerald: {
    iconBg: COLOR_BG.emerald,
    iconText: COLOR_TEXT.emerald,
    valueText: COLOR_TEXT_EMPHASIS.emerald,
  },
  indigo: {
    iconBg: COLOR_BG.indigo,
    iconText: COLOR_TEXT.indigo,
    valueText: COLOR_TEXT_EMPHASIS.indigo,
  },
  rose: {
    iconBg: COLOR_BG.rose,
    iconText: COLOR_TEXT.rose,
    valueText: COLOR_TEXT_EMPHASIS.rose,
  },
  violet: {
    iconBg: COLOR_BG.violet,
    iconText: COLOR_TEXT.violet,
    valueText: COLOR_TEXT_EMPHASIS.violet,
  },
};

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface MetricCardTrend {
  /** Display label, e.g. "+12.5%" or "-3". */
  value: string;
  direction: "up" | "down" | "neutral";
}

export interface MetricCardProps {
  label: ReactNode;
  value: ReactNode;
  /** Icon rendered inside a tinted pill. Omit for text-only cards. */
  icon?: ReactNode;
  /** @default "left" */
  iconPosition?: "left" | "right";
  /** Small muted text below the value. */
  subValue?: ReactNode;
  /** Custom content below the value. Takes precedence over `subValue`. */
  footer?: ReactNode;
  /** Optional trend indicator with directional arrow. */
  trend?: MetricCardTrend;
  variant?: MetricCardVariant;
  /**
   * `"sm"` — compact, muted background (default).
   * `"lg"` — larger value text, elevated white background with shadow.
   * @default "sm"
   */
  size?: "sm" | "lg";
  /** Transparent background with no border. */
  transparent?: boolean;
  className?: string;
}

function resolveBgClass(transparent: boolean, size: "sm" | "lg"): string {
  if (transparent) {
    return "bg-transparent border-transparent";
  }
  if (size === "lg") {
    return "border-slate-300 bg-white shadow-sm transition-colors hover:border-slate-400 dark:border-slate-700 dark:bg-arti-panel dark:hover:border-slate-600";
  }
  return "border-slate-300/80 bg-slate-50/70 dark:border-slate-700 dark:bg-arti-background";
}

/* ------------------------------------------------------------------ */
/*  Trend indicator                                                    */
/* ------------------------------------------------------------------ */

const TREND_COLORS: Record<MetricCardTrend["direction"], string> = {
  down: "text-rose-600 dark:text-rose-400",
  neutral: "text-slate-400 dark:text-slate-500",
  up: "text-emerald-600 dark:text-emerald-400",
};

const TrendArrow: FC<{ direction: MetricCardTrend["direction"] }> = ({
  direction,
}) => {
  if (direction === "neutral") {
    return (
      <svg
        className="h-3 w-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2.5}
          d="M5 12h14"
        />
      </svg>
    );
  }
  return (
    <svg
      className={`h-3 w-3 ${direction === "down" ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M5 15l7-7 7 7"
      />
    </svg>
  );
};

const TrendIndicator: FC<{ trend: MetricCardTrend }> = ({ trend }) => (
  <span
    className={`inline-flex items-center gap-0.5 font-mono text-[10px] font-bold ${TREND_COLORS[trend.direction]}`}
  >
    <TrendArrow direction={trend.direction} />
    {trend.value}
  </span>
);

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const MetricCard: FC<MetricCardProps> = ({
  label,
  value,
  icon,
  iconPosition = "left",
  subValue,
  footer,
  trend,
  variant = "default",
  size = "sm",
  transparent = false,
  className = "",
}) => {
  const colors = COLORS[resolveVariant(variant)];

  const bg = resolveBgClass(transparent, size);

  const valueCls =
    size === "lg"
      ? `mt-1.5 text-xl font-bold tabular-nums ${colors.valueText}`
      : `font-semibold ${colors.valueText}`;

  const bottomContent =
    footer ?? (subValue ? <Text as="p">{subValue}</Text> : null);

  const iconEl = icon ? (
    <div
      className={`flex-shrink-0 rounded-none border border-current/15 p-2 ${colors.iconBg}`}
    >
      <div
        className={`${size === "lg" ? "h-5 w-5" : "h-4 w-4"} ${colors.iconText}`}
      >
        {icon}
      </div>
    </div>
  ) : null;

  const textStack = (
    <div className="min-w-0 flex-1">
      <Text variant="caption" as="p" className="uppercase tracking-wide">
        {label}
      </Text>
      <div className="flex items-baseline gap-2">
        <p className={valueCls}>{value}</p>
        {trend ? <TrendIndicator trend={trend} /> : null}
      </div>
      {bottomContent}
    </div>
  );

  if (!iconEl) {
    return (
      <div className={`rounded-none border p-3 ${bg} ${className}`.trim()}>
        {textStack}
      </div>
    );
  }

  return (
    <div className={`rounded-none border p-3 ${bg} ${className}`.trim()}>
      <div
        className={`flex items-start gap-3 ${iconPosition === "right" ? "justify-between" : ""}`}
      >
        {iconPosition === "right" ? (
          <>
            {textStack}
            {iconEl}
          </>
        ) : (
          <>
            {iconEl}
            {textStack}
          </>
        )}
      </div>
    </div>
  );
};
